import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  ArrowLeft, Eye, Save, Plus, Trash2, GripVertical, Copy,
  Type, AlignLeft, CheckSquare, Circle, ChevronDown, Calendar,
  Upload, Star, SlidersHorizontal, Settings, Link as LinkIcon, Users, X,
  Clock, Hash, Mail, Phone, Grid3X3, LayoutGrid, Minus, ShieldCheck, Trophy, CheckCircle2, Bell, GitBranch
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';

import { API_URL } from '../utils/api';

const FIELD_TYPES = [
  { type: 'short_text', label: 'Short Text', icon: Type },
  { type: 'paragraph', label: 'Paragraph', icon: AlignLeft },
  { type: 'number', label: 'Number', icon: Hash },
  { type: 'email', label: 'Email', icon: Mail },
  { type: 'phone', label: 'Phone', icon: Phone },
  { type: 'multiple_choice', label: 'Multiple Choice', icon: Circle },
  { type: 'checkbox', label: 'Checkbox', icon: CheckSquare },
  { type: 'dropdown', label: 'Dropdown', icon: ChevronDown },
  { type: 'date', label: 'Date', icon: Calendar },
  { type: 'time', label: 'Time', icon: Clock },
  { type: 'file', label: 'File Upload', icon: Upload },
  { type: 'rating', label: 'Rating', icon: Star },
  { type: 'linear_scale', label: 'Linear Scale', icon: SlidersHorizontal },
  { type: 'multiple_choice_grid', label: 'Multiple Choice Grid', icon: Grid3X3 },
  { type: 'checkbox_grid', label: 'Checkbox Grid', icon: LayoutGrid },
  { type: 'section', label: 'Section', icon: Minus },
];

const generateId = () => Math.random().toString(36).substr(2, 9);

const FormBuilderPage = () => {
  const { formId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeFieldId, setActiveFieldId] = useState(null);
  const [showZohoSettings, setShowZohoSettings] = useState(false);
  const [zohoSheetId, setZohoSheetId] = useState('');
  const [zohoWorksheetName, setZohoWorksheetName] = useState('');
  const [googleSheetId, setGoogleSheetId] = useState('');
  const [googleWorksheetName, setGoogleWorksheetName] = useState('');
  const [showGoogleSettings, setShowGoogleSettings] = useState(false);
  const [zohoConnected, setZohoConnected] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [showCollaboratorsDialog, setShowCollaboratorsDialog] = useState(false);
  const [newCollaboratorEmail, setNewCollaboratorEmail] = useState('');
  const [addingCollaborator, setAddingCollaborator] = useState(false);
  const [resyncingZoho, setResyncingZoho] = useState(false);
  const [creatingWorksheet, setCreatingWorksheet] = useState(false);
  const [creatingWorkbook, setCreatingWorkbook] = useState(false);
  const [newWorkbookName, setNewWorkbookName] = useState('');

  const fetchForm = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/forms/${formId}`, {
        withCredentials: true
      });
      setForm(response.data);
      setZohoSheetId(response.data.zoho_sheet_id || '');
      setZohoWorksheetName(response.data.zoho_worksheet_name || '');
      setGoogleSheetId(response.data.google_sheet_id || '');
      setGoogleWorksheetName(response.data.google_worksheet_name || '');
    } catch (error) {
      toast.error('Failed to load form');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [formId, navigate]);

  const fetchZohoStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/zoho/status`, {
        withCredentials: true
      });
      setZohoConnected(response.data.connected);
    } catch (error) {
      console.error('Error fetching Zoho status:', error);
    }
  }, []);

  useEffect(() => {
    fetchForm();
    fetchZohoStatus();
  }, [fetchForm, fetchZohoStatus]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/forms/${formId}`, {
        title: form.title,
        description: form.description,
        fields: form.fields,
        zoho_sheet_id: zohoSheetId || null,
        zoho_worksheet_name: zohoWorksheetName || null,
        google_sheet_id: googleSheetId || null,
        google_worksheet_name: googleWorksheetName || null,
        collect_email: form.collect_email || false,
        is_quiz: form.is_quiz || false,
        release_marks: form.release_marks || 'immediate',
        show_missed_questions: form.show_missed_questions !== false,
        show_correct_answers: form.show_correct_answers !== false,
        show_point_values: form.show_point_values !== false,
        notify_owner: form.notify_owner || false,
        send_respondent_copy: form.send_respondent_copy || false
      }, { withCredentials: true });
      toast.success('Form saved');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map(d => d.msg || d).join(', ') : 'Failed to save form');
      console.error('Save error:', error?.response?.data);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleAddField = (type) => {
    const newField = {
      id: generateId(),
      type,
      label: type === 'section' ? 'Section Title' : 'Question',
      description: '',
      required: false,
      options: ['multiple_choice', 'checkbox', 'dropdown'].includes(type)
        ? [{ id: generateId(), label: 'Option 1', value: 'option_1' }]
        : [],
      min_value: type === 'linear_scale' ? 1 : 1,
      max_value: type === 'linear_scale' ? 5 : 5,
      min_label: '',
      max_label: '',
      rows: ['multiple_choice_grid', 'checkbox_grid'].includes(type) ? ['Row 1'] : [],
      columns: ['multiple_choice_grid', 'checkbox_grid'].includes(type) ? ['Column 1'] : [],
      validation: null
    };
    setForm(prev => ({
      ...prev,
      fields: [...prev.fields, newField]
    }));
    setActiveFieldId(newField.id);
  };

  const handleUpdateField = (fieldId, updates) => {
    setForm(prev => ({
      ...prev,
      fields: prev.fields.map(f => 
        f.id === fieldId ? { ...f, ...updates } : f
      )
    }));
  };

  const handleDeleteField = (fieldId) => {
    setForm(prev => ({
      ...prev,
      fields: prev.fields.filter(f => f.id !== fieldId)
    }));
    if (activeFieldId === fieldId) {
      setActiveFieldId(null);
    }
  };

  const handleDuplicateField = (field) => {
    const newField = {
      ...field,
      id: generateId(),
      options: field.options?.map(opt => ({ ...opt, id: generateId() })) || []
    };
    const index = form.fields.findIndex(f => f.id === field.id);
    const newFields = [...form.fields];
    newFields.splice(index + 1, 0, newField);
    setForm(prev => ({ ...prev, fields: newFields }));
    setActiveFieldId(newField.id);
  };

  const handleAddOption = (fieldId) => {
    const field = form.fields.find(f => f.id === fieldId);
    const optionNum = (field.options?.length || 0) + 1;
    const newOption = {
      id: generateId(),
      label: `Option ${optionNum}`,
      value: `option_${optionNum}`
    };
    handleUpdateField(fieldId, {
      options: [...(field.options || []), newOption]
    });
  };

  const handleUpdateOption = (fieldId, optionId, label) => {
    const field = form.fields.find(f => f.id === fieldId);
    handleUpdateField(fieldId, {
      options: field.options.map(opt =>
        opt.id === optionId ? { ...opt, label, value: label.toLowerCase().replace(/\s+/g, '_') } : opt
      )
    });
  };

  const handleDeleteOption = (fieldId, optionId) => {
    const field = form.fields.find(f => f.id === fieldId);
    if (field.options.length <= 1) return;
    handleUpdateField(fieldId, {
      options: field.options.filter(opt => opt.id !== optionId)
    });
  };

  const handleUpdateOptionGoTo = (fieldId, optionId, goToSection) => {
    const field = form.fields.find(f => f.id === fieldId);
    handleUpdateField(fieldId, {
      options: field.options.map(opt =>
        opt.id === optionId ? { ...opt, go_to_section: goToSection || null } : opt
      )
    });
  };

  // Compute section names for "Go to section" feature
  // Must match splitIntoSections logic in PublicFormPage
  const getSectionNames = () => {
    const names = [];
    let sectionCount = 0;
    const fields = form?.fields || [];
    
    // First check if there are any section fields
    const hasSectionFields = fields.some(f => f.type === 'section');
    if (!hasSectionFields) return [];
    
    // Walk through fields same as splitIntoSections
    let currentHasFields = false;
    let currentTitle = null;
    
    for (const field of fields) {
      if (field.type === 'section') {
        // Push the previous section
        if (currentHasFields || sectionCount === 0) {
          names.push({ 
            index: sectionCount, 
            label: sectionCount === 0 ? 'Section 1 (Start)' : `Section ${sectionCount + 1}${currentTitle && currentTitle !== 'Section Title' ? ': ' + currentTitle : ''}`
          });
          sectionCount++;
        }
        currentTitle = field.label;
        currentHasFields = false;
      } else {
        currentHasFields = true;
      }
    }
    // Push the last section
    if (currentHasFields || currentTitle) {
      names.push({ 
        index: sectionCount, 
        label: `Section ${sectionCount + 1}${currentTitle && currentTitle !== 'Section Title' ? ': ' + currentTitle : ''}`
      });
    }
    
    return names;
  };

  const sectionNames = form ? getSectionNames() : [];

  const handleCopyLink = () => {
    const link = `${window.location.origin}/forms/${formId}`;
    setShareLink(link);
    setShowShareDialog(true);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      toast.success('Link copied!');
    } catch (err) {
      // Fallback: select the input text
      const input = document.getElementById('share-link-input');
      if (input) {
        input.select();
        document.execCommand('copy');
        toast.success('Link copied!');
      }
    }
  };

  const handleAddCollaborator = async () => {
    if (!newCollaboratorEmail.trim()) return;
    
    setAddingCollaborator(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/forms/${formId}/collaborators`,
        { email: newCollaboratorEmail.trim().toLowerCase() },
        { withCredentials: true }
      );
      setForm(prev => ({ ...prev, collaborators: response.data.collaborators }));
      setNewCollaboratorEmail('');
      toast.success('Collaborator added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add collaborator');
    } finally {
      setAddingCollaborator(false);
    }
  };

  const handleRemoveCollaborator = async (email) => {
    try {
      const response = await axios.delete(
        `${API_URL}/api/forms/${formId}/collaborators/${encodeURIComponent(email)}`,
        { withCredentials: true }
      );
      setForm(prev => ({ ...prev, collaborators: response.data.collaborators }));
      toast.success('Collaborator removed');
    } catch (error) {
      toast.error('Failed to remove collaborator');
    }
  };

  const handleCreateWorksheet = async () => {
    if (!zohoSheetId) {
      toast.error('Please enter Zoho Sheet ID first');
      return;
    }
    if (!form.fields || form.fields.length === 0) {
      toast.error('Please add at least one field to the form first');
      return;
    }

    // First save the form with the sheet ID
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/forms/${formId}`, {
        title: form.title,
        description: form.description,
        fields: form.fields,
        zoho_sheet_id: zohoSheetId
      }, { withCredentials: true });
    } catch (error) {
      toast.error('Failed to save form');
      setSaving(false);
      return;
    }
    setSaving(false);

    // Now create the worksheet
    setCreatingWorksheet(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/forms/${formId}/create-zoho-worksheet`,
        {},
        { withCredentials: true }
      );
      setZohoWorksheetName(response.data.worksheet_name);
      toast.success(`Worksheet "${response.data.worksheet_name}" created with headers!`);
      fetchForm(); // Refresh form data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create worksheet');
    } finally {
      setCreatingWorksheet(false);
    }
  };

  const handleCreateWorkbook = async () => {
    const name = newWorkbookName.trim() || form.title || 'Form Responses';
    setCreatingWorkbook(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/zoho/create-workbook?workbook_name=${encodeURIComponent(name)}`,
        {},
        { withCredentials: true }
      );
      if (response.data.success) {
        setZohoSheetId(response.data.resource_id);
        setNewWorkbookName('');
        toast.success(`Sheet "${name}" created! ID auto-filled.`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create Zoho Sheet');
    } finally {
      setCreatingWorkbook(false);
    }
  };

  const handleResyncZoho = async () => {
    setResyncingZoho(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/forms/${formId}/resync-zoho`,
        {},
        { withCredentials: true }
      );
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start re-sync');
    } finally {
      setResyncingZoho(false);
    }
  };

  const handleTestZohoConnection = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/zoho/test-connection`, { withCredentials: true });
      if (response.data.token_valid) {
        toast.success('Zoho connection is working!');
      } else {
        toast.error(response.data.message || 'Zoho connection failed. Please re-connect.');
      }
    } catch (error) {
      toast.error('Failed to test Zoho connection');
    }
  };

  const handleTestZohoPush = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/forms/${formId}/test-zoho-push`, {}, { withCredentials: true });
      const result = response.data;
      if (result.zoho_response?.status === 'success') {
        toast.success('Test row pushed to Zoho successfully! Check your sheet.');
      } else {
        toast.error(`Zoho push failed: ${result.zoho_response?.error_message || JSON.stringify(result.zoho_response)}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Test push failed');
    }
  };

  const handleCreateGoogleSpreadsheet = async () => {
    try {
      const name = form.title || 'Form Responses';
      const response = await axios.post(`${API_URL}/api/google/create-spreadsheet?name=${encodeURIComponent(name)}`, {}, { withCredentials: true });
      if (response.data.success) {
        setGoogleSheetId(response.data.spreadsheet_id);
        toast.success(`Google Sheet "${name}" created!`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create Google Sheet');
    }
  };

  const handleCreateGoogleWorksheet = async () => {
    if (!googleSheetId) { toast.error('Enter Google Sheet ID first'); return; }
    try {
      // Save the sheet ID first
      await axios.put(`${API_URL}/api/forms/${formId}`, { ...form, google_sheet_id: googleSheetId }, { withCredentials: true });
      const response = await axios.post(`${API_URL}/api/forms/${formId}/create-google-worksheet`, {}, { withCredentials: true });
      setGoogleWorksheetName(response.data.worksheet_name);
      toast.success(`Worksheet "${response.data.worksheet_name}" created!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create worksheet');
    }
  };

  const handleTestGooglePush = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/forms/${formId}/test-google-push`, {}, { withCredentials: true });
      if (response.data.status === 'success') {
        toast.success('Test row pushed to Google Sheets!');
      } else {
        toast.error(`Google push failed: ${response.data.message}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Test push failed');
    }
  };

  const handleResyncGoogle = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/forms/${formId}/resync-google`, {}, { withCredentials: true });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Resync failed');
    }
  };

  const moveField = (fromIndex, toIndex) => {
    const newFields = [...form.fields];
    const [removed] = newFields.splice(fromIndex, 1);
    newFields.splice(toIndex, 0, removed);
    setForm(prev => ({ ...prev, fields: newFields }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F4F5]" data-testid="form-builder-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="text-slate-600"
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </div>

            <div className="flex items-center gap-2">
              {form?.is_owner && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCollaboratorsDialog(true)}
                  className={form?.collaborators?.length ? 'border-blue-300 text-blue-700 bg-blue-50' : ''}
                  data-testid="collaborators-btn"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Collaborators {form?.collaborators?.length ? `(${form.collaborators.length})` : ''}
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowZohoSettings(true)}
                className={zohoSheetId ? 'border-green-300 text-green-700 bg-green-50' : ''}
                data-testid="zoho-settings-btn"
              >
                <Settings className="w-4 h-4 mr-2" />
                Zoho
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowGoogleSettings(true)}
                className={googleSheetId ? 'border-blue-300 text-blue-700 bg-blue-50' : ''}
                data-testid="google-settings-btn"
              >
                <Settings className="w-4 h-4 mr-2" />
                Google Sheets
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyLink}
                data-testid="copy-form-link"
              >
                <LinkIcon className="w-4 h-4 mr-2" />
                Copy Link
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/forms/${formId}`)}
                data-testid="preview-form-btn"
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="save-form-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Form Title Card */}
        <Card className="mb-4 border-slate-200 shadow-sm overflow-hidden" data-testid="form-title-card">
          <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-indigo-600 rounded-l-xl"></div>
          <CardContent className="p-6 pl-8">
            <Input
              value={form.title}
              onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Form Title"
              className="text-2xl font-semibold font-['Outfit'] border-0 border-b border-transparent hover:border-slate-200 focus:border-indigo-600 rounded-none px-0 bg-transparent focus-visible:ring-0 transition-colors"
              data-testid="form-title-input"
            />
            <Input
              value={form.description || ''}
              onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Form description (optional)"
              className="mt-2 text-slate-500 border-0 border-b border-transparent hover:border-slate-200 focus:border-indigo-600 rounded-none px-0 bg-transparent focus-visible:ring-0 transition-colors"
              data-testid="form-description-input"
            />
          </CardContent>
        </Card>

        {/* Form Settings - Collect Email */}
        <Card className="mb-4 border-slate-200 shadow-sm" data-testid="form-settings-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-indigo-600" />
                <div>
                  <p className="text-sm font-medium text-slate-900">Collect email addresses</p>
                  <p className="text-xs text-slate-500">Require respondents to provide their email</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.collect_email || false}
                  onChange={(e) => setForm(prev => ({ ...prev, collect_email: e.target.checked }))}
                  className="sr-only peer"
                  data-testid="collect-email-toggle"
                />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Quiz Settings */}
        <Card className="mb-4 border-slate-200 shadow-sm" data-testid="quiz-settings-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Trophy className="w-5 h-5 text-amber-500" />
                <div>
                  <p className="text-sm font-medium text-slate-900">Make this a quiz</p>
                  <p className="text-xs text-slate-500">Assign points and set correct answers</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_quiz || false}
                  onChange={(e) => setForm(prev => ({ ...prev, is_quiz: e.target.checked }))}
                  className="sr-only peer"
                  data-testid="quiz-mode-toggle"
                />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
              </label>
            </div>

            {form.is_quiz && (
              <div className="space-y-4 pt-3 border-t border-slate-100">
                {/* Release Marks */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-slate-700">Release marks</p>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="release_marks" value="immediate" checked={form.release_marks !== 'manual'} onChange={() => setForm(prev => ({ ...prev, release_marks: 'immediate' }))} className="w-4 h-4 text-amber-500" />
                      <span className="text-sm text-slate-600">Immediately after each submission</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="release_marks" value="manual" checked={form.release_marks === 'manual'} onChange={() => setForm(prev => ({ ...prev, release_marks: 'manual' }))} className="w-4 h-4 text-amber-500" />
                      <span className="text-sm text-slate-600">Later, after manual review</span>
                    </label>
                  </div>
                </div>

                {/* Respondent Settings */}
                <div className="space-y-3">
                  <p className="text-sm font-medium text-slate-700">Respondents can see</p>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.show_missed_questions !== false} onChange={(e) => setForm(prev => ({ ...prev, show_missed_questions: e.target.checked }))} className="w-4 h-4 rounded text-amber-500" />
                    <span className="text-sm text-slate-600">Missed questions</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.show_correct_answers !== false} onChange={(e) => setForm(prev => ({ ...prev, show_correct_answers: e.target.checked }))} className="w-4 h-4 rounded text-amber-500" />
                    <span className="text-sm text-slate-600">Correct answers</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.show_point_values !== false} onChange={(e) => setForm(prev => ({ ...prev, show_point_values: e.target.checked }))} className="w-4 h-4 rounded text-amber-500" />
                    <span className="text-sm text-slate-600">Point values</span>
                  </label>
                </div>

                {/* Total Points Display */}
                <div className="bg-amber-50 rounded-lg p-3 flex items-center justify-between">
                  <span className="text-sm font-medium text-amber-800">Total points</span>
                  <span className="text-lg font-bold text-amber-600" data-testid="total-points">
                    {form.fields.reduce((sum, f) => sum + (f.points || 0), 0)}
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Email Notification Settings */}
        <Card className="mb-4 border-slate-200 shadow-sm" data-testid="notification-settings-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <Bell className="w-5 h-5 text-blue-500" />
              <p className="text-sm font-medium text-slate-900">Email Notifications</p>
            </div>
            <div className="space-y-3">
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm text-slate-600">Notify form owner on new response</span>
                <input type="checkbox" checked={form.notify_owner || false} onChange={(e) => setForm(prev => ({ ...prev, notify_owner: e.target.checked }))} className="w-4 h-4 rounded text-blue-600" data-testid="notify-owner-toggle" />
              </label>
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm text-slate-600">Send respondent a copy of their response</span>
                <input type="checkbox" checked={form.send_respondent_copy || false} onChange={(e) => setForm(prev => ({ ...prev, send_respondent_copy: e.target.checked }))} className="w-4 h-4 rounded text-blue-600" data-testid="send-respondent-copy-toggle" />
              </label>
              {form.send_respondent_copy && !form.collect_email && (
                <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">Enable "Collect email addresses" above to send respondent copies.</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Fields */}
        {form.fields.map((field, index) => (
          <FieldCard
            key={field.id}
            field={field}
            index={index}
            isActive={activeFieldId === field.id}
            onActivate={() => setActiveFieldId(field.id)}
            onUpdate={(updates) => handleUpdateField(field.id, updates)}
            onDelete={() => handleDeleteField(field.id)}
            onDuplicate={() => handleDuplicateField(field)}
            onAddOption={() => handleAddOption(field.id)}
            onUpdateOption={(optionId, label) => handleUpdateOption(field.id, optionId, label)}
            onDeleteOption={(optionId) => handleDeleteOption(field.id, optionId)}
            onUpdateOptionGoTo={(optionId, goTo) => handleUpdateOptionGoTo(field.id, optionId, goTo)}
            onMoveUp={() => index > 0 && moveField(index, index - 1)}
            onMoveDown={() => index < form.fields.length - 1 && moveField(index, index + 1)}
            sectionNames={sectionNames}
            isQuiz={form.is_quiz || false}
            allFields={form.fields}
          />
        ))}

        {/* Add Field Button */}
        <Card className="border-dashed border-2 border-slate-300 bg-white/50 hover:bg-white transition-colors" data-testid="add-field-card">
          <CardContent className="p-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="w-full h-12 text-slate-500 hover:text-indigo-600" data-testid="add-field-btn">
                  <Plus className="w-5 h-5 mr-2" />
                  Add Question
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56">
                {FIELD_TYPES.map((fieldType) => (
                  <DropdownMenuItem
                    key={fieldType.type}
                    onClick={() => handleAddField(fieldType.type)}
                    data-testid={`add-field-${fieldType.type}`}
                  >
                    <fieldType.icon className="w-4 h-4 mr-2" />
                    {fieldType.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </CardContent>
        </Card>
      </main>

      {/* Zoho Settings Dialog */}
      <Dialog open={showZohoSettings} onOpenChange={setShowZohoSettings}>
        <DialogContent data-testid="zoho-settings-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Zoho Sheets Integration</DialogTitle>
          </DialogHeader>
          
          {!zohoConnected ? (
            <div className="py-4 text-center">
              <p className="text-slate-500 mb-4">
                Connect your Zoho account first to sync responses.
              </p>
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                Go to Dashboard to Connect
              </Button>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              {/* Step 1: Auto-create or manually enter Zoho Sheet ID */}
              <div className="p-4 border border-blue-200 bg-blue-50 rounded-lg space-y-3">
                <p className="font-medium text-blue-900">Step 1: Get a Zoho Sheet</p>
                <p className="text-xs text-blue-700">Auto-create a new sheet or paste an existing Sheet ID.</p>
                <div className="flex gap-2">
                  <Input
                    value={newWorkbookName}
                    onChange={(e) => setNewWorkbookName(e.target.value)}
                    placeholder={form?.title || 'Sheet name (optional)'}
                    className="flex-1 bg-white"
                    data-testid="new-workbook-name-input"
                  />
                  <Button
                    onClick={handleCreateWorkbook}
                    disabled={creatingWorkbook}
                    className="whitespace-nowrap bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="create-workbook-btn"
                  >
                    {creatingWorkbook ? 'Creating...' : 'Create New Sheet'}
                  </Button>
                </div>
              </div>

              {/* Step 2: Enter Sheet ID */}
              <div className="space-y-2">
                <Label htmlFor="zoho-sheet-id">Step 2: Zoho Sheet ID {zohoSheetId && <span className="text-green-600 text-xs ml-1">(filled)</span>}</Label>
                <Input
                  id="zoho-sheet-id"
                  value={zohoSheetId}
                  onChange={(e) => setZohoSheetId(e.target.value)}
                  placeholder="e.g., 6fmw025dc969fa1e649f7b0a6fdebddb06afd"
                  data-testid="zoho-sheet-id-input"
                />
                <p className="text-xs text-slate-400">Auto-filled when you create a new sheet, or paste an existing ID manually</p>
              </div>
              
              {/* Step 3: Auto-create worksheet */}
              <div className="space-y-2">
                <Label htmlFor="zoho-worksheet">Step 3: Create Worksheet with Headers</Label>
                <div className="flex gap-2">
                  <Input
                    id="zoho-worksheet"
                    value={zohoWorksheetName}
                    onChange={(e) => setZohoWorksheetName(e.target.value)}
                    placeholder="Auto-generated worksheet name"
                    data-testid="zoho-worksheet-input"
                    className="flex-1"
                    readOnly={!!zohoWorksheetName}
                  />
                  <Button
                    variant="outline"
                    onClick={handleCreateWorksheet}
                    disabled={creatingWorksheet || !zohoSheetId}
                    className="whitespace-nowrap bg-green-50 border-green-300 text-green-700 hover:bg-green-100"
                    data-testid="create-worksheet-btn"
                  >
                    {creatingWorksheet ? 'Creating...' : 'Auto Create'}
                  </Button>
                </div>
                <p className="text-xs text-slate-400">
                  This will create a new worksheet with your form field names as headers
                </p>
              </div>

              {zohoWorksheetName && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg space-y-2">
                  <p className="text-sm text-green-700">
                    Configured to sync with worksheet: <strong>{zohoWorksheetName}</strong>
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleTestZohoConnection}
                      className="text-green-600 border-green-200 hover:bg-green-50"
                      data-testid="test-zoho-btn"
                    >
                      Test Connection
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleTestZohoPush}
                      className="text-amber-600 border-amber-200 hover:bg-amber-50"
                      data-testid="test-zoho-push-btn"
                    >
                      Test Push Row
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleResyncZoho}
                      disabled={resyncingZoho}
                      className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                      data-testid="resync-zoho-btn"
                    >
                      {resyncingZoho ? 'Starting re-sync...' : 'Re-sync All Responses to Zoho'}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500">
                    Use "Test Connection" to verify Zoho is accessible. Use "Re-sync" if responses are missing from Zoho Sheet.
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowZohoSettings(false)}>
              Cancel
            </Button>
            {zohoConnected && (
              <Button
                onClick={() => {
                  handleSave();
                  setShowZohoSettings(false);
                }}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="save-zoho-settings"
              >
                Save Settings
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Google Sheets Settings Dialog */}
      <Dialog open={showGoogleSettings} onOpenChange={setShowGoogleSettings}>
        <DialogContent className="max-w-lg" data-testid="google-settings-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Google Sheets Settings</DialogTitle>
            <DialogDescription>
              Configure Google Sheets sync for this form
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Step 1: Create or enter a Google Spreadsheet</Label>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={handleCreateGoogleSpreadsheet} data-testid="create-google-sheet-btn">
                  Auto Create Spreadsheet
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="google-sheet-id">Google Spreadsheet ID {googleSheetId && <span className="text-green-600 text-xs ml-1">(filled)</span>}</Label>
              <Input
                id="google-sheet-id"
                value={googleSheetId}
                onChange={(e) => setGoogleSheetId(e.target.value)}
                placeholder="Paste spreadsheet ID from URL"
                data-testid="google-sheet-id-input"
              />
              <p className="text-xs text-slate-500">From URL: docs.google.com/spreadsheets/d/<strong>THIS_PART</strong>/edit</p>
            </div>

            <div className="space-y-2">
              <Label>Step 2: Create Worksheet with Headers</Label>
              <div className="flex gap-2 items-center">
                <Input
                  value={googleWorksheetName}
                  placeholder="Auto-generated after creation"
                  readOnly={!!googleWorksheetName}
                  className="flex-1"
                  data-testid="google-worksheet-name"
                />
                <Button type="button" variant="outline" size="sm" onClick={handleCreateGoogleWorksheet} disabled={!googleSheetId} data-testid="create-google-worksheet-btn">
                  Auto Create
                </Button>
              </div>
            </div>

            {googleWorksheetName && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg space-y-2">
                <p className="text-sm text-green-700">
                  Configured to sync with worksheet: <strong>{googleWorksheetName}</strong>
                </p>
                <div className="flex gap-2 flex-wrap">
                  <Button variant="outline" size="sm" onClick={handleTestGooglePush} className="text-amber-600 border-amber-200 hover:bg-amber-50" data-testid="test-google-push-btn">
                    Test Push Row
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleResyncGoogle} className="text-indigo-600 border-indigo-200 hover:bg-indigo-50" data-testid="resync-google-btn">
                    Re-sync All Responses
                  </Button>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" onClick={() => { setShowGoogleSettings(false); handleSave(); }} data-testid="save-google-settings-btn">
              Save & Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Share Link Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Share Form</DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <p className="text-sm text-slate-500">
              Share this link with anyone to collect responses:
            </p>
            <div className="flex gap-2">
              <Input
                id="share-link-input"
                value={shareLink}
                readOnly
                className="flex-1"
                onClick={(e) => e.target.select()}
              />
              <Button
                onClick={copyToClipboard}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShareDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Collaborators Dialog */}
      <Dialog open={showCollaboratorsDialog} onOpenChange={setShowCollaboratorsDialog}>
        <DialogContent data-testid="collaborators-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Share with Collaborators</DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <p className="text-sm text-slate-500">
              Add collaborators by email. They will be able to edit this form and view responses.
            </p>
            
            {/* Add Collaborator Input */}
            <div className="flex gap-2">
              <Input
                type="email"
                value={newCollaboratorEmail}
                onChange={(e) => setNewCollaboratorEmail(e.target.value)}
                placeholder="Enter email address"
                className="flex-1"
                onKeyDown={(e) => e.key === 'Enter' && handleAddCollaborator()}
                data-testid="collaborator-email-input"
              />
              <Button
                onClick={handleAddCollaborator}
                disabled={addingCollaborator || !newCollaboratorEmail.trim()}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="add-collaborator-btn"
              >
                {addingCollaborator ? 'Adding...' : 'Add'}
              </Button>
            </div>

            {/* Collaborators List */}
            {form?.collaborators?.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm text-slate-600">Current Collaborators</Label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {form.collaborators.map((email) => (
                    <div
                      key={email}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                      data-testid={`collaborator-${email}`}
                    >
                      <span className="text-sm text-slate-700">{email}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveCollaborator(email)}
                        className="h-8 w-8 p-0 text-slate-400 hover:text-red-500"
                        data-testid={`remove-collaborator-${email}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(!form?.collaborators || form.collaborators.length === 0) && (
              <div className="text-center py-6 text-slate-400">
                <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No collaborators yet</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCollaboratorsDialog(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Field Card Component
const VALIDATION_TYPES = {
  short_text: [
    { value: 'none', label: 'No validation' },
    { value: 'length', label: 'Length' },
    { value: 'regex', label: 'Regular expression' },
    { value: 'email_format', label: 'Email format' },
    { value: 'url_format', label: 'URL format' },
    { value: 'number_format', label: 'Contains number' },
  ],
  paragraph: [
    { value: 'none', label: 'No validation' },
    { value: 'length', label: 'Length' },
  ],
  number: [
    { value: 'none', label: 'No validation' },
    { value: 'range', label: 'Number range' },
  ],
  checkbox: [
    { value: 'none', label: 'No validation' },
    { value: 'select_count', label: 'Selection count' },
  ],
};

const FieldCard = ({ 
  field, 
  index,
  isActive, 
  onActivate, 
  onUpdate, 
  onDelete, 
  onDuplicate,
  onAddOption,
  onUpdateOption,
  onDeleteOption,
  onUpdateOptionGoTo,
  onMoveUp,
  onMoveDown,
  sectionNames,
  isQuiz,
  allFields
}) => {
  const fieldTypeConfig = FIELD_TYPES.find(ft => ft.type === field.type);
  const Icon = fieldTypeConfig?.icon || Type;

  const handleAddRow = () => {
    const rows = [...(field.rows || [])];
    rows.push(`Row ${rows.length + 1}`);
    onUpdate({ rows });
  };
  const handleUpdateRow = (idx, val) => {
    const rows = [...(field.rows || [])];
    rows[idx] = val;
    onUpdate({ rows });
  };
  const handleDeleteRow = (idx) => {
    if ((field.rows || []).length <= 1) return;
    const rows = (field.rows || []).filter((_, i) => i !== idx);
    onUpdate({ rows });
  };
  const handleAddColumn = () => {
    const columns = [...(field.columns || [])];
    columns.push(`Column ${columns.length + 1}`);
    onUpdate({ columns });
  };
  const handleUpdateColumn = (idx, val) => {
    const columns = [...(field.columns || [])];
    columns[idx] = val;
    onUpdate({ columns });
  };
  const handleDeleteColumn = (idx) => {
    if ((field.columns || []).length <= 1) return;
    const columns = (field.columns || []).filter((_, i) => i !== idx);
    onUpdate({ columns });
  };

  // Section divider has a unique card style
  if (field.type === 'section') {
    return (
      <Card
        className={`mb-4 transition-all border-t-4 border-t-indigo-500 ${isActive ? 'shadow-lg ring-1 ring-slate-200' : 'hover:shadow-md cursor-pointer'}`}
        onClick={() => !isActive && onActivate()}
        data-testid={`field-card-${field.id}`}
      >
        <CardContent className="p-6">
          {isActive ? (
            <div className="space-y-2">
              <Input value={field.label} onChange={(e) => onUpdate({ label: e.target.value })} placeholder="Section Title" className="text-xl font-semibold border-0 border-b border-transparent hover:border-slate-200 focus:border-indigo-600 rounded-none px-0 bg-transparent focus-visible:ring-0" data-testid={`field-label-input-${field.id}`} />
              <Input value={field.description || ''} onChange={(e) => onUpdate({ description: e.target.value })} placeholder="Section description (optional)" className="text-sm text-slate-500 border-0 border-b border-transparent hover:border-slate-200 focus:border-indigo-600 rounded-none px-0 bg-transparent focus-visible:ring-0" data-testid={`field-description-input-${field.id}`} />
              <div className="flex justify-end pt-2">
                <Button variant="ghost" size="sm" onClick={onDelete} className="text-slate-500 hover:text-red-600" data-testid={`delete-field-${field.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-xl font-semibold text-slate-900">{field.label}</p>
              {field.description && <p className="text-sm text-slate-500 mt-1">{field.description}</p>}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  const renderFieldPreview = () => {
    switch (field.type) {
      case 'short_text':
        return <Input disabled placeholder="Short answer text" className="max-w-md bg-slate-50" />;
      case 'paragraph':
        return <Textarea disabled placeholder="Long answer text" className="bg-slate-50" rows={3} />;
      case 'number':
        return <Input disabled type="number" placeholder="Number" className="max-w-md bg-slate-50" />;
      case 'email':
        return <Input disabled type="email" placeholder="name@example.com" className="max-w-md bg-slate-50" />;
      case 'phone':
        return <Input disabled type="tel" placeholder="+1 (555) 000-0000" className="max-w-md bg-slate-50" />;
      case 'time':
        return <Input disabled type="time" className="max-w-md bg-slate-50" />;
      case 'multiple_choice':
      case 'checkbox':
        return (
          <div className="space-y-2">
            {field.options?.map((option) => (
              <div key={option.id} className="flex items-center gap-3">
                <div className={`w-5 h-5 rounded-${field.type === 'checkbox' ? 'sm' : 'full'} border-2 border-slate-300 flex-shrink-0`} />
                {isActive ? (
                  <div className="flex items-center gap-2 flex-1">
                    <Input value={option.label} onChange={(e) => onUpdateOption(option.id, e.target.value)} className="flex-1 h-8" data-testid={`option-input-${option.id}`} />
                    {field.type === 'multiple_choice' && sectionNames.length > 0 && (
                      <Select value={option.go_to_section || ''} onValueChange={(val) => onUpdateOptionGoTo(option.id, val === 'next' ? null : val)}>
                        <SelectTrigger className="w-[180px] h-8 text-xs" data-testid={`goto-section-${option.id}`}>
                          <SelectValue placeholder="Go to section" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="next">Continue to next</SelectItem>
                          {sectionNames.map(s => (
                            <SelectItem key={s.index} value={String(s.index)}>{s.label}</SelectItem>
                          ))}
                          <SelectItem value="submit">Submit form</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    {field.options.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => onDeleteOption(option.id)} className="h-8 w-8 p-0 text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></Button>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 flex-1">
                    <span className="text-slate-600">{option.label}</span>
                    {option.go_to_section && (
                      <span className="text-xs text-indigo-500 bg-indigo-50 px-2 py-0.5 rounded">
                        {option.go_to_section === 'submit' ? 'Submit form' : `Go to Section ${parseInt(option.go_to_section) + 1}`}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
            {isActive && (
              <Button variant="ghost" size="sm" onClick={onAddOption} className="text-indigo-600 hover:text-indigo-700 ml-8" data-testid="add-option-btn"><Plus className="w-4 h-4 mr-1" />Add option</Button>
            )}
          </div>
        );
      case 'dropdown':
        return (
          <div className="space-y-2">
            <Select disabled><SelectTrigger className="max-w-md bg-slate-50"><SelectValue placeholder="Select an option" /></SelectTrigger></Select>
            {isActive && (
              <div className="space-y-2 mt-4">
                {field.options?.map((option, i) => (
                  <div key={option.id} className="flex items-center gap-3">
                    <span className="w-6 text-slate-400 text-sm">{i + 1}.</span>
                    <Input value={option.label} onChange={(e) => onUpdateOption(option.id, e.target.value)} className="flex-1 h-8" data-testid={`dropdown-option-${option.id}`} />
                    {sectionNames.length > 0 && (
                      <Select value={option.go_to_section || ''} onValueChange={(val) => onUpdateOptionGoTo(option.id, val === 'next' ? null : val)}>
                        <SelectTrigger className="w-[180px] h-8 text-xs" data-testid={`goto-section-dropdown-${option.id}`}>
                          <SelectValue placeholder="Go to section" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="next">Continue to next</SelectItem>
                          {sectionNames.map(s => (
                            <SelectItem key={s.index} value={String(s.index)}>{s.label}</SelectItem>
                          ))}
                          <SelectItem value="submit">Submit form</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    {field.options.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => onDeleteOption(option.id)} className="h-8 w-8 p-0 text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></Button>
                    )}
                  </div>
                ))}
                <Button variant="ghost" size="sm" onClick={onAddOption} className="text-indigo-600 hover:text-indigo-700 ml-6"><Plus className="w-4 h-4 mr-1" />Add option</Button>
              </div>
            )}
          </div>
        );
      case 'date':
        return <Input type="date" disabled className="max-w-md bg-slate-50" />;
      case 'file':
        return (
          <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center max-w-md">
            <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <p className="text-sm text-slate-500">Click to upload or drag and drop</p>
          </div>
        );
      case 'rating':
        return (
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star key={star} className="w-8 h-8 text-slate-300" />
            ))}
          </div>
        );
      case 'linear_scale':
        return (
          <div className="space-y-2">
            <div className="flex items-center gap-4 max-w-md">
              {isActive ? (
                <>
                  <Select value={String(field.min_value)} onValueChange={(v) => onUpdate({ min_value: parseInt(v) })}>
                    <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
                    <SelectContent>{[0, 1].map(v => (<SelectItem key={v} value={String(v)}>{v}</SelectItem>))}</SelectContent>
                  </Select>
                  <span className="text-slate-400">to</span>
                  <Select value={String(field.max_value)} onValueChange={(v) => onUpdate({ max_value: parseInt(v) })}>
                    <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
                    <SelectContent>{[2, 3, 4, 5, 6, 7, 8, 9, 10].map(v => (<SelectItem key={v} value={String(v)}>{v}</SelectItem>))}</SelectContent>
                  </Select>
                </>
              ) : (
                <div className="flex gap-4">
                  {Array.from({ length: field.max_value - field.min_value + 1 }, (_, i) => (
                    <div key={i} className="w-10 h-10 rounded-full border-2 border-slate-300 flex items-center justify-center text-sm text-slate-600">{field.min_value + i}</div>
                  ))}
                </div>
              )}
            </div>
            {isActive && (
              <div className="flex gap-4 mt-2">
                <div className="flex-1">
                  <Label className="text-xs text-slate-400">Min label (optional)</Label>
                  <Input value={field.min_label || ''} onChange={(e) => onUpdate({ min_label: e.target.value })} placeholder="e.g., Not satisfied" className="h-8 mt-1" />
                </div>
                <div className="flex-1">
                  <Label className="text-xs text-slate-400">Max label (optional)</Label>
                  <Input value={field.max_label || ''} onChange={(e) => onUpdate({ max_label: e.target.value })} placeholder="e.g., Very satisfied" className="h-8 mt-1" />
                </div>
              </div>
            )}
          </div>
        );
      case 'multiple_choice_grid':
      case 'checkbox_grid':
        return (
          <div className="space-y-3">
            {!isActive ? (
              <div className="overflow-x-auto">
                <table className="text-sm border-collapse">
                  <thead>
                    <tr>
                      <th className="p-2"></th>
                      {(field.columns || []).map((col, i) => <th key={i} className="p-2 text-center text-slate-600 font-medium">{col}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {(field.rows || []).map((row, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="p-2 text-slate-700">{row}</td>
                        {(field.columns || []).map((_, j) => (
                          <td key={j} className="p-2 text-center">
                            <div className={`w-5 h-5 mx-auto rounded-${field.type === 'checkbox_grid' ? 'sm' : 'full'} border-2 border-slate-300`} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label className="text-xs text-slate-500 font-medium mb-1 block">Rows</Label>
                  {(field.rows || []).map((row, i) => (
                    <div key={i} className="flex items-center gap-2 mb-1">
                      <Input value={row} onChange={(e) => handleUpdateRow(i, e.target.value)} className="flex-1 h-8" data-testid={`grid-row-${i}`} />
                      {(field.rows || []).length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteRow(i)} className="h-8 w-8 p-0 text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></Button>
                      )}
                    </div>
                  ))}
                  <Button variant="ghost" size="sm" onClick={handleAddRow} className="text-indigo-600" data-testid="add-grid-row"><Plus className="w-4 h-4 mr-1" />Add row</Button>
                </div>
                <div>
                  <Label className="text-xs text-slate-500 font-medium mb-1 block">Columns</Label>
                  {(field.columns || []).map((col, i) => (
                    <div key={i} className="flex items-center gap-2 mb-1">
                      <Input value={col} onChange={(e) => handleUpdateColumn(i, e.target.value)} className="flex-1 h-8" data-testid={`grid-col-${i}`} />
                      {(field.columns || []).length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteColumn(i)} className="h-8 w-8 p-0 text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></Button>
                      )}
                    </div>
                  ))}
                  <Button variant="ghost" size="sm" onClick={handleAddColumn} className="text-indigo-600" data-testid="add-grid-col"><Plus className="w-4 h-4 mr-1" />Add column</Button>
                </div>
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  const renderValidationSettings = () => {
    const validationOptions = VALIDATION_TYPES[field.type];
    if (!validationOptions || !isActive) return null;
    
    const currentValidation = field.validation || {};
    const validationType = currentValidation.type || 'none';

    return (
      <div className="pt-3 border-t border-slate-100 mt-3" data-testid={`validation-section-${field.id}`}>
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck className="w-4 h-4 text-slate-400" />
          <Label className="text-xs text-slate-500 font-medium">Response Validation</Label>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          <Select value={validationType} onValueChange={(v) => onUpdate({ validation: v === 'none' ? null : { type: v } })}>
            <SelectTrigger className="w-44 h-8 text-xs" data-testid={`validation-type-${field.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {validationOptions.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {(validationType === 'length' || validationType === 'select_count') && (
            <>
              <Input type="number" placeholder="Min" value={currentValidation.min || ''} onChange={(e) => onUpdate({ validation: { ...currentValidation, min: e.target.value ? parseInt(e.target.value) : undefined } })} className="w-20 h-8 text-xs" data-testid={`validation-min-${field.id}`} />
              <span className="text-xs text-slate-400">to</span>
              <Input type="number" placeholder="Max" value={currentValidation.max || ''} onChange={(e) => onUpdate({ validation: { ...currentValidation, max: e.target.value ? parseInt(e.target.value) : undefined } })} className="w-20 h-8 text-xs" data-testid={`validation-max-${field.id}`} />
            </>
          )}
          {validationType === 'range' && (
            <>
              <Input type="number" placeholder="Min value" value={currentValidation.min || ''} onChange={(e) => onUpdate({ validation: { ...currentValidation, min: e.target.value ? parseFloat(e.target.value) : undefined } })} className="w-24 h-8 text-xs" data-testid={`validation-min-${field.id}`} />
              <span className="text-xs text-slate-400">to</span>
              <Input type="number" placeholder="Max value" value={currentValidation.max || ''} onChange={(e) => onUpdate({ validation: { ...currentValidation, max: e.target.value ? parseFloat(e.target.value) : undefined } })} className="w-24 h-8 text-xs" data-testid={`validation-max-${field.id}`} />
            </>
          )}
          {validationType === 'regex' && (
            <Input placeholder="Regular expression" value={currentValidation.pattern || ''} onChange={(e) => onUpdate({ validation: { ...currentValidation, pattern: e.target.value } })} className="flex-1 h-8 text-xs" data-testid={`validation-pattern-${field.id}`} />
          )}
          {validationType !== 'none' && (
            <Input placeholder="Custom error message (optional)" value={currentValidation.message || ''} onChange={(e) => onUpdate({ validation: { ...currentValidation, message: e.target.value } })} className="flex-1 h-8 text-xs" data-testid={`validation-message-${field.id}`} />
          )}
        </div>
      </div>
    );
  };

  return (
    <Card
      className={`mb-4 transition-all ${isActive ? 'shadow-lg ring-1 ring-slate-200' : 'hover:shadow-md cursor-pointer'}`}
      onClick={() => !isActive && onActivate()}
      data-testid={`field-card-${field.id}`}
    >
      {isActive && <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-indigo-600 rounded-l-xl" />}
      
      <CardContent className={`p-6 ${isActive ? 'pl-8' : ''}`}>
        {isActive && (
          <div className="flex justify-center -mt-2 mb-2">
            <div className="flex gap-1 text-slate-300 cursor-grab"><GripVertical className="w-5 h-5" /></div>
          </div>
        )}

        <div className="flex items-start gap-4 mb-4">
          <div className="flex-1">
            {isActive ? (
              <Input value={field.label} onChange={(e) => onUpdate({ label: e.target.value })} placeholder="Question" className="text-lg font-medium border-0 border-b border-transparent hover:border-slate-200 focus:border-indigo-600 rounded-none px-0 bg-transparent focus-visible:ring-0" data-testid={`field-label-input-${field.id}`} />
            ) : (
              <p className="text-lg font-medium text-slate-900">{field.label}{field.required && <span className="text-red-500 ml-1">*</span>}</p>
            )}
            {isActive && (
              <Input value={field.description || ''} onChange={(e) => onUpdate({ description: e.target.value })} placeholder="Description (optional)" className="mt-1 text-sm text-slate-500 border-0 border-b border-transparent hover:border-slate-200 focus:border-indigo-600 rounded-none px-0 bg-transparent focus-visible:ring-0" data-testid={`field-description-input-${field.id}`} />
            )}
          </div>
          {isActive && (
            <Select value={field.type} onValueChange={(type) => {
              const updates = { type };
              if (['multiple_choice', 'checkbox', 'dropdown'].includes(type)) {
                updates.options = field.options?.length ? field.options : [{ id: generateId(), label: 'Option 1', value: 'option_1' }];
              } else {
                updates.options = [];
              }
              if (['multiple_choice_grid', 'checkbox_grid'].includes(type)) {
                updates.rows = field.rows?.length ? field.rows : ['Row 1'];
                updates.columns = field.columns?.length ? field.columns : ['Column 1'];
              }
              onUpdate(updates);
            }}>
              <SelectTrigger className="w-48" data-testid={`field-type-select-${field.id}`}>
                <Icon className="w-4 h-4 mr-2" /><SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FIELD_TYPES.map((ft) => (
                  <SelectItem key={ft.type} value={ft.type}><div className="flex items-center"><ft.icon className="w-4 h-4 mr-2" />{ft.label}</div></SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="mb-4">{renderFieldPreview()}</div>

        {isActive && (
          <>
            {/* Quiz: Answer Key & Points */}
            {isQuiz && field.type !== 'section' && field.type !== 'file' && (
              <div className="pt-3 border-t border-slate-100 mt-3" data-testid={`answer-key-section-${field.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium text-slate-700">Answer key</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">Points:</span>
                    <Input
                      type="number"
                      min="0"
                      value={field.points || 0}
                      onChange={(e) => onUpdate({ points: parseInt(e.target.value) || 0 })}
                      className="w-16 h-7 text-sm text-center"
                      data-testid={`field-points-${field.id}`}
                    />
                  </div>
                </div>
                {/* Correct answer input based on field type */}
                {(field.type === 'multiple_choice' || field.type === 'dropdown') && (
                  <div className="bg-green-50 rounded-lg p-3 space-y-2">
                    <p className="text-xs text-green-700 font-medium mb-2">Select the correct answer:</p>
                    {field.options?.map((opt) => (
                      <label key={opt.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name={`correct-${field.id}`}
                          checked={field.correct_answer === opt.value}
                          onChange={() => onUpdate({ correct_answer: opt.value })}
                          className="w-4 h-4 text-green-600"
                        />
                        <span className="text-sm text-slate-700">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                )}
                {field.type === 'checkbox' && (
                  <div className="bg-green-50 rounded-lg p-3 space-y-2">
                    <p className="text-xs text-green-700 font-medium mb-2">Select all correct answers:</p>
                    {field.options?.map((opt) => (
                      <label key={opt.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={(field.correct_answer || []).includes(opt.value)}
                          onChange={(e) => {
                            const current = field.correct_answer || [];
                            const updated = e.target.checked
                              ? [...current, opt.value]
                              : current.filter(v => v !== opt.value);
                            onUpdate({ correct_answer: updated });
                          }}
                          className="w-4 h-4 rounded text-green-600"
                        />
                        <span className="text-sm text-slate-700">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                )}
                {(field.type === 'short_text' || field.type === 'paragraph' || field.type === 'number') && (
                  <div className="bg-green-50 rounded-lg p-3">
                    <p className="text-xs text-green-700 font-medium mb-2">Correct answer (exact match):</p>
                    <Input
                      value={field.correct_answer || ''}
                      onChange={(e) => onUpdate({ correct_answer: e.target.value })}
                      placeholder="Enter the correct answer"
                      className="bg-white"
                      data-testid={`correct-answer-input-${field.id}`}
                    />
                  </div>
                )}
              </div>
            )}
            {renderValidationSettings()}
            
            {/* Conditional Logic */}
            {field.type !== 'section' && (
              <div className="pt-3 border-t border-slate-100 mt-3" data-testid={`conditional-logic-${field.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <GitBranch className="w-4 h-4 text-purple-500" />
                    <span className="text-sm font-medium text-slate-700">Show this field only when</span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" checked={!!field.conditional_logic} onChange={(e) => {
                      if (e.target.checked) {
                        const triggerField = allFields.find(f => f.id !== field.id && f.type !== 'section' && ['multiple_choice', 'checkbox', 'dropdown', 'short_text', 'number'].includes(f.type));
                        onUpdate({ conditional_logic: triggerField ? { field_id: triggerField.id, operator: 'equals', value: '' } : { field_id: '', operator: 'equals', value: '' } });
                      } else {
                        onUpdate({ conditional_logic: null });
                      }
                    }} className="sr-only peer" />
                    <div className="w-9 h-5 bg-slate-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-purple-500"></div>
                  </label>
                </div>
                {field.conditional_logic && (
                  <div className="bg-purple-50 rounded-lg p-3 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Select value={field.conditional_logic.field_id || ''} onValueChange={(val) => onUpdate({ conditional_logic: { ...field.conditional_logic, field_id: val, value: '' } })}>
                        <SelectTrigger className="w-[180px] h-8 text-xs"><SelectValue placeholder="Select field" /></SelectTrigger>
                        <SelectContent>
                          {allFields.filter(f => f.id !== field.id && f.type !== 'section').map(f => (
                            <SelectItem key={f.id} value={f.id}>{f.label || f.type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select value={field.conditional_logic.operator || 'equals'} onValueChange={(val) => onUpdate({ conditional_logic: { ...field.conditional_logic, operator: val } })}>
                        <SelectTrigger className="w-[130px] h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="equals">equals</SelectItem>
                          <SelectItem value="not_equals">not equals</SelectItem>
                          <SelectItem value="contains">contains</SelectItem>
                        </SelectContent>
                      </Select>
                      {(() => {
                        const triggerField = allFields.find(f => f.id === field.conditional_logic.field_id);
                        if (triggerField && triggerField.options?.length > 0) {
                          return (
                            <Select value={field.conditional_logic.value || ''} onValueChange={(val) => onUpdate({ conditional_logic: { ...field.conditional_logic, value: val } })}>
                              <SelectTrigger className="w-[180px] h-8 text-xs"><SelectValue placeholder="Select value" /></SelectTrigger>
                              <SelectContent>
                                {triggerField.options.map(o => (
                                  <SelectItem key={o.id} value={o.value}>{o.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          );
                        }
                        return <Input value={field.conditional_logic.value || ''} onChange={(e) => onUpdate({ conditional_logic: { ...field.conditional_logic, value: e.target.value } })} placeholder="Value" className="w-[180px] h-8 text-xs" />;
                      })()}
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="flex items-center justify-between pt-4 border-t border-slate-100 mt-3">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch id={`required-${field.id}`} checked={field.required} onCheckedChange={(checked) => onUpdate({ required: checked })} data-testid={`field-required-switch-${field.id}`} />
                  <Label htmlFor={`required-${field.id}`} className="text-sm text-slate-600">Required</Label>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={onDuplicate} className="text-slate-500 hover:text-indigo-600" data-testid={`duplicate-field-${field.id}`}><Copy className="w-4 h-4" /></Button>
                <Button variant="ghost" size="sm" onClick={onDelete} className="text-slate-500 hover:text-red-600" data-testid={`delete-field-${field.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default FormBuilderPage;
